/**
 * Authenticated app shell.
 *
 * Layout per ux-design/responsive.md, as three genuinely different trees
 * rather than one tree hidden by CSS:
 *
 *   desktop (1280+)   docked left sidebar — drag-resizable (180–420px),
 *                     collapsible to a 68px icon rail, both persisted.
 *                     Game-day surfaces (Play Entry / Coaches Highlight) keep
 *                     the icon rail and temporarily fly it out in place to
 *                     show labels — never the phone nav drawer.
 *   tablet (640–1279) the same nav as an off-canvas drawer behind a
 *                     hamburger, with a backdrop and focus return; bottom
 *                     tab bar persists.
 *   phone (<640)      that drawer plus the 5-slot bottom tab bar.
 *
 * The nav previously lived in the header: 18 links wrapped into stacked rows
 * and pushed the page down. A vertical rail is the layout that actually fits
 * that many destinations, and it groups them (Game day / Team /
 * Administration) so the list is scannable.
 *
 * Always present, on every authenticated non-game-day page: the single
 * header "Help" entry (AC-HELP-001 — the former floating "?" FAB duplicated it,
 * overlapped content and had an undersized dismiss control, so it was
 * removed).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';

import {
  DEFAULT_HELP_TAB,
  HelpCenterOverlay,
  toHelpTab,
  type HelpTabId,
} from '../components/help-center-overlay';
import { Tooltip, useTooltipOverrides } from '../components/tooltip';
import { useEdgeSwipe } from '../hooks/use-edge-swipe';
import { useIsDesktop, useIsPhone } from '../hooks/use-media-query';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_DEFAULT_WIDTH,
  useSidebarLayout,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
} from '../hooks/use-sidebar-layout';
import { PRODUCT_NAME, ROUTES } from '../lib/constants';
import { navItemsFor, phoneTabsFor } from '../lib/nav-items';
import { PERSONA_LABELS, type Persona } from '../lib/personas';

import { AccountMenu } from './account-menu';
import { useActiveEvent } from './active-event-context';
import { ActiveEventSwitcher } from './active-event-switcher';
import { SHELL_GD_CENTER_ID, SHELL_GD_END_ID, SHELL_GD_STATUS_ID } from './game-day-chrome';
import { useApiClient } from './providers';
import { useSession } from './session-context';
import { SidebarNav } from './sidebar-nav';
import { useIsForeignTenantRoute, useTenantSlug } from './tenant-slug';

export function AppShell({
  /** Portal title shown in the breadcrumb trail (team name or portal name). */
  portalLabel,
  /** Tenant-scoped nav is suppressed for cross-tenant portals (admin, fanatic). */
  hasTenantNav = true,
}: {
  portalLabel?: string;
  hasTenantNav?: boolean;
}): JSX.Element {
  const { claims, persona: sessionPersona } = useSession();
  // Validated against the session — never the raw `:tenantSlug` param. Chrome
  // (breadcrumb, nav links, event switcher) must name the team whose data the
  // API is returning; see `tenant-slug.ts`.
  const tenantSlug = useTenantSlug();
  const isForeignTenantRoute = useIsForeignTenantRoute();
  const location = useLocation();
  const isGameDayRoute = /\/(play-entry|coaches-highlight)(\/|$)/.test(location.pathname);
  const { activeEventId } = useActiveEvent();
  /**
   * Play Entry + Coaches Highlight with a LIVE event: one-viewport board
   * surfaces (locked design) — compact header (no wordmark / team name /
   * Help), icon rail. With NO event selected there is no board to focus on,
   * so these routes keep the standard chrome: full header (brand, team name,
   * event switcher, Help, Account) and the expanded, labelled sidebar. The
   * "pick an event" notice in the page body is then the only thing that
   * differs from any other authenticated page.
   */
  const isGameDaySurface = isGameDayRoute && activeEventId !== null;
  /**
   * Desk density (compact controls, 14px UI text) applies to the mouse and
   * keyboard pages of a tenant. Play Entry, Coaches Highlight and Play
   * Archive are live/touch surfaces with locked sizing; the setup wizard is
   * a guided flow. Those never get `shell--desk`.
   */
  const isDeskRoute =
    hasTenantNav &&
    /^\/app\/[^/]+(\/|$)/.test(location.pathname) &&
    !/\/(play-entry|coaches-highlight|play-archive|welcome)(\/|$)/.test(location.pathname);

  const persona: Persona = claims?.persona ?? sessionPersona ?? 'coach';
  const isDesktop = useIsDesktop();
  const isPhone = useIsPhone();
  const apiClient = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  /** Game-day desktop: temporary expand of the docked icon rail (not the phone drawer). */
  const [isRailFlyoutOpen, setIsRailFlyoutOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarCollapseRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  /** The docked rail's scrollable nav list (`.sidebar__nav`). */
  const sidebarNavRef = useRef<HTMLElement>(null);

  const sidebar = useSidebarLayout();

  // The docked rail is its own scroll container, so a scroll offset picked up
  // on one page would otherwise persist into the next and clip Home / Game
  // day off the top (seen at 1280×720). Every route change starts from the top.
  useEffect(() => {
    const nav = sidebarNavRef.current;
    if (nav) nav.scrollTop = 0;
  }, [location.pathname]);

  // Contextual tooltip text (tenant > platform > shipped default) is hydrated
  // once here, so every control on every authenticated page shows the wording
  // this tenant configured.
  useTooltipOverrides(
    useCallback((signal) => apiClient.getTooltips(undefined, signal), [apiClient])
  );

  // Navigating dismisses the drawer; without this it stays open over the page
  // the user just asked for.
  useEffect(() => {
    setIsDrawerOpen(false);
    setIsRailFlyoutOpen(false);
  }, [location.pathname]);

  // A drawer that survives a resize past the desktop breakpoint would leave a
  // backdrop over a layout that already shows the docked rail.
  useEffect(() => {
    if (isDesktop) setIsDrawerOpen(false);
  }, [isDesktop]);

  // Game-day rail flyout is ephemeral — drop it when leaving the surface or
  // dropping below the desktop breakpoint (drawer owns nav there).
  useEffect(() => {
    if (!isDesktop || !isGameDaySurface) setIsRailFlyoutOpen(false);
  }, [isDesktop, isGameDaySurface]);

  // Escape closes drawer / rail flyout; focus returns to the opener.
  useEffect(() => {
    if (!isDrawerOpen && !isRailFlyoutOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsDrawerOpen(false);
        setIsRailFlyoutOpen(false);
        (menuButtonRef.current ?? sidebarCollapseRef.current)?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    // Move focus into the drawer so the next Tab lands on a nav link rather
    // than continuing through the header behind the backdrop.
    if (isDrawerOpen) drawerRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDrawerOpen, isRailFlyoutOpen]);

  // The open tab lives in the URL, so the Help Center survives a refresh and
  // can be linked to (AC-HELP-001).
  const helpTab = toHelpTab(searchParams.get('help'));
  const isHelpOpen = helpTab !== null;

  function setHelpTab(tab: HelpTabId | null): void {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (tab === null) {
          for (const key of [...next.keys()].filter((key) => key.startsWith('help'))) {
            next.delete(key);
          }
        } else next.set('help', tab);
        return next;
      },
      { replace: true }
    );
  }

  const slug = tenantSlug;
  const label = portalLabel ?? (slug === null ? PERSONA_LABELS[persona] : slug);
  const hasNav = hasTenantNav && slug !== null && navItemsFor(persona).length > 0;

  // Tablet: a swipe in from the left edge opens the same drawer the menu
  // button does, so a two-handed operator (Play Entry cards, Stat Tracker)
  // never has to reach for the header. Touch events only — a mouse cannot
  // trigger it, and the phone uses its bottom tab bar instead.
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  useEdgeSwipe({
    edge: 'left',
    onSwipe: openDrawer,
    isEnabled: hasNav && !isDesktop && !isPhone && !isDrawerOpen,
  });
  // Bottom link bar: phone + tablet. Hidden on desktop where the rail owns nav.
  // Desktop must not mount these links (a11y tree duplicates).
  const phoneTabs = hasTenantNav && slug !== null && !isDesktop ? phoneTabsFor(persona) : [];

  const isRailDocked = hasNav && isDesktop;
  // Play Entry / Coaches Highlight reclaim width: icon rail by default.
  // Collapse control temporarily flies the same rail out with labels —
  // never mounts the phone nav drawer on desktop.
  const isIconRail = isGameDaySurface ? !isRailFlyoutOpen : sidebar.isCollapsed;
  const railWidth = isIconRail
    ? SIDEBAR_COLLAPSED_WIDTH
    : isGameDaySurface
      ? SIDEBAR_DEFAULT_WIDTH
      : sidebar.width;

  // Runtime assertion behind `RequireTenantAccess`: if a route ever mounts the
  // shell for a slug this session cannot open, render no chrome at all and let
  // `/post-auth` dispatch to the right portal. Mixed tenant chrome is never an
  // acceptable fallback.
  if (isForeignTenantRoute) return <Navigate to={ROUTES.postAuth} replace />;

  return (
    <div
      className={[
        'shell',
        isRailDocked ? 'shell--railed' : '',
        sidebar.isDragging ? 'shell--dragging' : '',
        isDeskRoute ? 'shell--desk' : '',
        isGameDaySurface ? 'shell--game-day' : '',
        isGameDaySurface ? 'shell--play-entry-chrome' : '',
        phoneTabs.length > 0 ? 'shell--bottom-tabs' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={isRailDocked ? ({ ['--shell-rail-width']: `${railWidth}px` } as never) : undefined}
    >
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <header className="shell__header">
        {hasNav && !isDesktop ? (
          <button
            type="button"
            ref={menuButtonRef}
            className="shell__action shell__menu-button"
            aria-label="Open navigation menu"
            aria-expanded={isDrawerOpen}
            aria-controls="app-nav-drawer"
            onClick={() => setIsDrawerOpen(true)}
          >
            <span aria-hidden="true">☰</span>
          </button>
        ) : null}

        <NavLink to="/" className="shell__brand">
          <img src="/images/logo-mark.png" alt="" width={28} height={28} />
          <span>{PRODUCT_NAME}</span>
        </NavLink>
        <ol className="shell__breadcrumbs" aria-label="Breadcrumb">
          <li>{label}</li>
        </ol>
        {/* FR-EVENTS-08: the active-event context switcher is persistent on
            every tenant-scoped page, for every persona. */}
        {hasTenantNav && slug !== null ? <ActiveEventSwitcher tenantSlug={slug} /> : null}
        {/* Play Entry chrome collapse: Connected / health lands here. */}
        <span id={SHELL_GD_STATUS_ID} className="shell-gd__status-slot" />
        <span className="shell__spacer" />
        {/* Play Entry: PHASE + TYPE chips (portaled). */}
        <span id={SHELL_GD_CENTER_ID} className="shell-gd__center-slot" />
        <span className="shell__spacer shell__spacer--end" />
        {/* Play Entry: session chip + Clear + Log (portaled). */}
        <span id={SHELL_GD_END_ID} className="shell-gd__end-slot" />
        {/* Desk pages: who is signed in (the same facts the Account menu
            shows) fills the header's right side; hidden elsewhere by CSS. */}
        {isDeskRoute ? (
          <span className="shell__identity" data-testid="shell-identity">
            <span className="shell__identity-email">{claims?.email ?? ''}</span>
            <span className="shell__identity-role">{PERSONA_LABELS[persona]}</span>
          </span>
        ) : null}
        <div className="shell__actions">
          {isGameDaySurface ? null : (
            <>
              <button
                type="button"
                className="shell__action"
                aria-haspopup="dialog"
                aria-expanded={isHelpOpen}
                onClick={() => setHelpTab(DEFAULT_HELP_TAB)}
              >
                <span aria-hidden="true">?</span>
                <span className="shell__action-label">Help</span>
              </button>
              <Tooltip tooltipId="help.center.entry" label="Help" position="bottom" />
            </>
          )}
          <AccountMenu
            email={claims?.email ?? 'Signed in'}
            persona={persona}
            tenantLabel={slug === null ? null : `Team code: ${slug}`}
          />
        </div>
      </header>

      <div className="shell__body">
        {isRailDocked ? (
          <aside
            className={`sidebar${isIconRail ? ' sidebar--collapsed' : ''}${
              isGameDaySurface && isRailFlyoutOpen ? ' sidebar--flyout' : ''
            }`}
            aria-label="Primary navigation"
          >
            <div className="sidebar__head">
              <button
                type="button"
                ref={sidebarCollapseRef}
                className="sidebar__collapse"
                aria-label={
                  isGameDaySurface
                    ? isRailFlyoutOpen
                      ? 'Collapse sidebar'
                      : 'Expand sidebar'
                    : isIconRail
                      ? 'Expand sidebar'
                      : 'Collapse sidebar'
                }
                aria-pressed={isIconRail}
                aria-expanded={isGameDaySurface ? isRailFlyoutOpen : undefined}
                onClick={() => {
                  if (isGameDaySurface) {
                    setIsRailFlyoutOpen((open) => !open);
                    return;
                  }
                  sidebar.toggleCollapsed();
                }}
                title={
                  isGameDaySurface
                    ? 'Expand navigation rail (game day keeps the icon rail compact)'
                    : undefined
                }
              >
                <span aria-hidden="true">{isIconRail ? '»' : '«'}</span>
              </button>
            </div>
            <SidebarNav
              ref={sidebarNavRef}
              persona={persona}
              tenantSlug={slug as string}
              variant="docked"
              isCollapsed={isIconRail}
              onNavigate={
                isGameDaySurface && isRailFlyoutOpen ? () => setIsRailFlyoutOpen(false) : undefined
              }
            />
            {/* Drag to resize; arrows resize by keyboard, Enter collapses. */}
            {isGameDaySurface ? null : (
              <div
                className="sidebar__resizer"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize sidebar"
                aria-valuenow={railWidth}
                aria-valuemin={SIDEBAR_MIN_WIDTH}
                aria-valuemax={SIDEBAR_MAX_WIDTH}
                tabIndex={0}
                onPointerDown={sidebar.onHandlePointerDown}
                onKeyDown={sidebar.onHandleKeyDown}
                onDoubleClick={sidebar.toggleCollapsed}
              />
            )}
          </aside>
        ) : null}

        <main className="shell__main" id="main-content">
          <Outlet />
        </main>
      </div>

      {isRailDocked && isRailFlyoutOpen ? (
        <button
          type="button"
          className="sidebar-flyout-backdrop"
          aria-label="Close navigation menu"
          onClick={() => {
            setIsRailFlyoutOpen(false);
            sidebarCollapseRef.current?.focus();
          }}
        />
      ) : null}

      {hasNav && !isDesktop && isDrawerOpen ? (
        <div className="nav-drawer">
          <button
            type="button"
            className="nav-drawer__backdrop"
            aria-label="Close navigation menu"
            onClick={() => {
              setIsDrawerOpen(false);
              menuButtonRef.current?.focus();
            }}
          />
          <div
            className="nav-drawer__panel"
            id="app-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            tabIndex={-1}
            ref={drawerRef}
          >
            <div className="nav-drawer__head">
              <span className="nav-drawer__title">{label}</span>
              <button
                type="button"
                className="nav-drawer__close"
                aria-label="Close navigation menu"
                onClick={() => {
                  setIsDrawerOpen(false);
                  menuButtonRef.current?.focus();
                }}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <SidebarNav
              persona={persona}
              tenantSlug={slug as string}
              variant="drawer"
              onNavigate={() => setIsDrawerOpen(false)}
            />
          </div>
        </div>
      ) : null}

      {phoneTabs.length > 0 ? (
        <nav className="shell__tabs" aria-label="Sections">
          {phoneTabs.map((item) => (
            <NavLink
              key={item.id}
              to={item.view === '' ? `/app/${slug}` : `/app/${slug}/${item.view}`}
              end={item.view === ''}
              className="shell__tab"
            >
              <span className="shell__tab-icon" aria-hidden="true">
                {item.icon}
              </span>
              <span>{item.shortLabel}</span>
            </NavLink>
          ))}
        </nav>
      ) : null}

      {helpTab === null ? null : (
        <HelpCenterOverlay
          persona={persona}
          activeTab={helpTab}
          onTabChange={(tab) => setHelpTab(tab)}
          onClose={() => setHelpTab(null)}
        />
      )}
    </div>
  );
}
